"use client";
import { ComboboxItemValueContext, useComboboxScopedContext } from "./combobox-context.js";
import { createElement, createHook, forwardRef } from "@ariakit/react-utils";
import { normalizeString, removeUndefinedValues, toArray } from "@ariakit/utils";
import { useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/combobox/combobox-item-value.tsx
const TagName = "span";
function normalizeValue(value) {
	return normalizeString(value).toLowerCase();
}
function getOffsets(string, values) {
	const offsets = [];
	for (const value of values) {
		if (!value) continue;
		let pos = 0;
		const length = value.length;
		let index = string.indexOf(value, pos);
		while (index !== -1) {
			offsets.push([index, length]);
			pos = index + 1;
			index = string.indexOf(value, pos);
		}
	}
	return offsets;
}
function mergeOverlappingOffsets(offsets) {
	offsets.sort(([a], [b]) => a - b);
	const merged = [];
	for (const [offset, length] of offsets) {
		const last = merged[merged.length - 1];
		if (last && offset < last[0] + last[1]) last[1] = Math.max(last[1], offset + length - last[0]);
		else merged.push([offset, length]);
	}
	return merged;
}
function getNormalizedIndexes(itemValue) {
	const starts = [];
	const ends = [];
	let index = 0;
	for (const char of itemValue) {
		const normalizedLength = normalizeValue(char).length;
		for (let i = 0; i < normalizedLength; i += 1) {
			starts.push(i === 0 ? index : -1);
			ends.push(index);
		}
		index += char.length;
	}
	starts.push(itemValue.length);
	ends.push(itemValue.length);
	let nextBoundary = itemValue.length;
	for (let i = starts.length - 1; i >= 0; i -= 1) {
		const start = starts[i];
		if (start == null) continue;
		if (start === -1) starts[i] = nextBoundary;
		else nextBoundary = start;
	}
	return {
		starts,
		ends
	};
}
function toOriginalOffsets(itemValue, normalizedOffsets) {
	if (!normalizedOffsets.length) return normalizedOffsets;
	const { starts, ends } = getNormalizedIndexes(itemValue);
	const offsets = [];
	for (const [normalizedOffset, normalizedLength] of normalizedOffsets) {
		const start = starts[normalizedOffset];
		const end = ends[normalizedOffset + normalizedLength];
		if (start == null || end == null) continue;
		if (end <= start) continue;
		offsets.push([start, end - start]);
	}
	return offsets;
}
function splitValue(itemValue, userValue) {
	if (!itemValue) return itemValue;
	if (!userValue) return itemValue;
	const userValues = toArray(userValue).map(normalizeValue);
	const parts = [];
	const span = (value, autocomplete = false) => /* @__PURE__ */ jsx("span", {
		"data-autocomplete-value": autocomplete ? "" : void 0,
		"data-user-value": autocomplete ? void 0 : "",
		children: value
	}, parts.length);
	const offsets = toOriginalOffsets(itemValue, mergeOverlappingOffsets(getOffsets(normalizeValue(itemValue), new Set(userValues))));
	const firstEntry = offsets[0];
	if (!firstEntry) {
		parts.push(span(itemValue, true));
		return parts;
	}
	const [firstOffset] = firstEntry;
	[itemValue.slice(0, firstOffset), ...offsets.flatMap(([offset, length], i) => {
		const value = itemValue.slice(offset, offset + length);
		const nextOffset = offsets[i + 1]?.[0];
		return [value, itemValue.slice(offset + length, nextOffset)];
	})].forEach((value, i) => {
		if (!value) return;
		parts.push(span(value, i % 2 === 0));
	});
	return parts;
}
/**
* Returns props to create a `ComboboxItemValue` component that displays a value
* element inside a combobox item. The value will be split into span elements
* and returned as the children prop. The portions of the value that correspond
* to the store value will have a `data-user-value` attribute. The other
* portions will have a `data-autocomplete-value` attribute.
* @see https://ariakit.com/components/combobox
* @example
* ```jsx
* const store = useComboboxStore({ value: "p" });
* const props = useComboboxItemValue({ store, value: "Apple" });
* <Role {...props} />
* // This will result in the following DOM:
* <span>
*   <span data-autocomplete-value>A</span>
*   <span data-user-value>p</span>
*   <span data-user-value>p</span>
*   <span data-autocomplete-value>le</span>
* </span>
* ```
*/
const useComboboxItemValue = createHook(function useComboboxItemValue({ store, value, userValue, ...props }) {
	const context = useComboboxScopedContext();
	store = store || context;
	const itemContext = useContext(ComboboxItemValueContext);
	const itemValue = value ?? itemContext;
	const inputValue = useStoreState(store, (state) => userValue ?? state?.value);
	props = {
		children: useMemo(() => {
			if (!itemValue) return;
			if (!inputValue) return itemValue;
			return splitValue(itemValue, inputValue);
		}, [itemValue, inputValue]),
		...props
	};
	return removeUndefinedValues(props);
});
/**
* Renders a `span` element with the combobox item value as children. The value
* is split into span elements. Portions of the value matching the user input
* will have a
* [`data-user-value`](https://ariakit.com/guide/styling#data-user-value)
* attribute, while the rest will have a
* [`data-autocomplete-value`](https://ariakit.com/guide/styling#data-autocomplete-value)
* attribute.
*
* The item value is automatically set to the value of the closest
* [`ComboboxItem`](https://ariakit.com/reference/combobox-item) component's
* [`value`](https://ariakit.com/reference/combobox-item#value) prop. The user
* input value is automatically set to the combobox store's
* [`value`](https://ariakit.com/reference/use-combobox-store#value) state. Both
* values can be overridden by providing the
* [`value`](https://ariakit.com/reference/combobox-item-value#value) and
* [`userValue`](https://ariakit.com/reference/combobox-item-value#uservalue)
* props, respectively.
* @see https://ariakit.com/components/combobox
* @example
* ```jsx {5} "value"
* <ComboboxProvider value="p">
*   <Combobox />
*   <ComboboxPopover>
*     <ComboboxItem value="Apple">
*       <ComboboxItemValue />
*     </ComboboxItem>
*   </ComboboxPopover>
* </ComboboxProvider>
*
* // The Apple item will have a value element that looks like this:
* <span>
*   <span data-autocomplete-value>A</span>
*   <span data-user-value>p</span>
*   <span data-user-value>p</span>
*   <span data-autocomplete-value>le</span>
* </span>
* ```
*/
const ComboboxItemValue = forwardRef(function ComboboxItemValue(props) {
	return createElement(TagName, useComboboxItemValue(props));
});
//#endregion
export { ComboboxItemValue, useComboboxItemValue };

//# sourceMappingURL=combobox-item-value.js.map